import { useMutation } from '@tanstack/react-query';
import { LuCheckCircle } from 'react-icons/lu';
import { MdError, MdWarning } from 'react-icons/md';
import { toast } from 'react-toastify';
import { InputCheckoutProps } from '../@types/ServiceCheckoutInput';
import ReactIcon from '../components/ReactIcon';
import { Checkout } from '../services/salesShopItems.service';
import { getErrorMessage } from '../helpers/errorMessages';
import { useShoppingCartStore } from '../store/shoppingCart/shoppingCartStore';
import { useUserProfile } from './useUserData';
import { AccessProfile } from '@/helpers/accessProfileUtil';
import { usePaymentModalStore } from '../store/payment/paymentModalStore';

export const usePostCheckout = (isDisabled?: boolean) => {
    const setCheckoutId = useShoppingCartStore(state => state.setCheckoutId);
    const method = usePaymentModalStore(state => state.method);
    const userProfile = useUserProfile();

    const { data, isPending, mutateAsync } = useMutation({
        
        mutationFn: async (input: InputCheckoutProps) => {
            // Bloquear checkout com cupom para proprietários
            const user = userProfile.data;
            const isOwner = user && (
                user.accessProfile?.enumKey === AccessProfile.Owner ||
                user.accessProfile?.enumKey === AccessProfile.TAC_OWNER ||
                user.accessProfile?.enumKey === AccessProfile.Dependent ||
                user.accessProfile?.enumKey === AccessProfile.TAC_DEPENDENT
            );
            if (isOwner && (input.couponCode || input.discountCouponId)) {
                // limpar cupom do store e notificar
                useShoppingCartStore.getState().removeCoupon();
                throw new Error('Esse cupom não está disponível para seu perfil de acesso');
            }

            const response = await Checkout(input);
            setCheckoutId(response.saleId);
            return response;
        },
        onSuccess: () => {
            method === 'pix' &&
            toast.success(
                'Sucesso, você será redirecionado para o pagamento', {
                style: {
                    fontSize: '1rem',
                    fontWeight: '400',
                },
                position: 'top-right',
                autoClose: 5000,
                icon: (
                    <ReactIcon
                        Icon={LuCheckCircle}
                        size={'1.5rem'}
                        fill="#22C55E"
                        color="white"
                    />
                ),
            });
        },

        onError: (error: Error) => {
            const raw = error.message;

            // Special-case: if backend indicates missing billing address, show a warning toast with yellow icon
            if (raw === 'SalePayment.RequiredAddress') {
                const message = getErrorMessage(raw);
                toast.warning(() => message, {
                    toastId: 'checkout-required-address',
                    style: {
                        fontSize: '1rem',
                        fontWeight: '400',
                    },
                    position: 'top-right',
                    autoClose: 5000,
                    icon: (
                        <ReactIcon
                            Icon={MdWarning}
                            size={'1.5rem'}
                            fill="#FBBF24"
                            color="white"
                        />
                    ),
                });
                return;
            }

            const message = getErrorMessage(raw);

            toast.error(() => message, {
                toastId: 4,
                style: {
                    fontSize: '1rem',
                    fontWeight: '400',
                },
                position: 'top-right',
                autoClose: 5000,
                icon: (
                    <ReactIcon
                        Icon={MdError}
                        size={'1.5rem'}
                        fill="#e40505"
                        color="white"
                    />
                ),
            });
        },
        
    });

    if (isDisabled) {
        return {
            checkoutResponse: data,
            isPendingCheckout: isPending,
            mutateAsyncCheckout: async () => Promise.reject(new Error('Checkout desabilitado')),
        };
    }

    return {
        checkoutResponse: data,
        isPendingCheckout: isPending,
        mutateAsyncCheckout: mutateAsync,
    };
};
